import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Loader2, Plus, Save, Trash2, Upload, Link2, Image as ImageIcon, Eye, Monitor } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { api, apiError, fileUrl } from "@/lib/api";
import { AdminCard, Field, inputCls } from "@/pages/admin/ui";

const EMPTY_SLIDE = { image_url: "", media_kind: "image", link_url: "", duration_seconds: 6, is_active: true };

export default function AdminPromoPopup() {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploadingIdx, setUploadingIdx] = useState(-1);
  const fileRefs = useRef({});

  useEffect(() => {
    api.get("/admin/promo-popup").then(({ data }) => setCfg({
      enabled: !!data.enabled,
      delay_seconds: data.delay_seconds ?? 2,
      once_per_session: data.once_per_session !== false,
      slides: (data.slides || []).map((s) => ({ ...EMPTY_SLIDE, ...s })),
    })).catch((e) => toast.error(apiError(e)));
  }, []);

  const setF = (k, v) => setCfg((c) => ({ ...c, [k]: v }));
  const setSlide = (i, k, v) => setCfg((c) => ({ ...c, slides: c.slides.map((s, j) => (j === i ? { ...s, [k]: v } : s)) }));
  const addSlide = () => setCfg((c) => (c.slides.length >= 3 ? c : { ...c, slides: [...c.slides, { ...EMPTY_SLIDE }] }));
  const removeSlide = (i) => setCfg((c) => ({ ...c, slides: c.slides.filter((_, j) => j !== i) }));

  const upload = async (i, file) => {
    if (!file) return;
    setUploadingIdx(i);
    const body = new FormData();
    body.append("file", file);
    try {
      const { data } = await api.post("/admin/upload", body, { headers: { "Content-Type": "multipart/form-data" } });
      setCfg((c) => ({ ...c, slides: c.slides.map((s, j) => (j === i ? { ...s, image_url: data.url, media_kind: data.kind === "video" ? "video" : "image" } : s)) }));
      toast.success("Visuel téléversé ✅");
    } catch (e) {
      toast.error(apiError(e, "Upload impossible"));
    } finally {
      setUploadingIdx(-1);
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/admin/promo-popup", {
        enabled: cfg.enabled,
        delay_seconds: Number(cfg.delay_seconds) || 0,
        once_per_session: cfg.once_per_session,
        slides: cfg.slides.map((s) => ({
          id: s.id || "", image_url: s.image_url, media_kind: s.media_kind || "image",
          link_url: s.link_url || "", duration_seconds: Number(s.duration_seconds) || 5, is_active: s.is_active !== false,
        })),
      });
      setCfg({ enabled: !!data.enabled, delay_seconds: data.delay_seconds, once_per_session: data.once_per_session !== false,
        slides: (data.slides || []).map((s) => ({ ...EMPTY_SLIDE, ...s })) });
      toast.success("Fenêtre promo enregistrée ✅");
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };

  if (!cfg) return <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-brand" /></div>;

  return (
    <div className="space-y-6" data-testid="admin-promo-popup-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-display text-2xl font-extrabold text-ink">Fenêtre de publication (popup)</h1>
          <p className="mt-1 max-w-2xl text-sm text-slate-500">Affichez jusqu'à 3 visuels de promotion au centre du site (images, GIF ou vidéos), avec un lien cliquable et un délai par visuel. Adaptez, activez, puis enregistrez.</p>
        </div>
        <button onClick={save} disabled={saving} data-testid="promo-save"
          className="flex items-center gap-2 rounded-full bg-ink px-6 py-3 text-sm font-bold text-white transition-colors hover:bg-brand disabled:opacity-60">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Enregistrer
        </button>
      </div>

      <AdminCard title="Réglages généraux" description="Activation et comportement d'affichage.">
        <div className="grid gap-4 sm:grid-cols-3">
          <div className="flex items-center justify-between rounded-xl border border-line bg-surface px-4 py-3 sm:col-span-3">
            <div>
              <p className="text-sm font-bold text-ink">Activer la fenêtre promo</p>
              <p className="text-xs text-slate-500">Quand elle est activée, la fenêtre s'affiche aux visiteurs du site.</p>
            </div>
            <Switch checked={cfg.enabled} onCheckedChange={(v) => setF("enabled", v)} data-testid="promo-enabled" />
          </div>
          <Field label="Délai avant affichage (secondes)" hint="Temps après l'ouverture de la page avant que la fenêtre apparaisse.">
            <input type="number" min={0} max={60} className={inputCls} value={cfg.delay_seconds}
              onChange={(e) => setF("delay_seconds", e.target.value)} data-testid="promo-delay" />
          </Field>
          <label className="flex items-center justify-between rounded-xl border border-line px-4 py-3 text-sm sm:col-span-2">
            <span className="font-semibold text-ink">Afficher une seule fois par session</span>
            <Switch checked={cfg.once_per_session} onCheckedChange={(v) => setF("once_per_session", v)} data-testid="promo-once" />
          </label>
        </div>
      </AdminCard>

      <AdminCard title={`Visuels de promotion (${cfg.slides.length}/3)`} description="Téléversez vos images/vidéos depuis votre ordinateur. Formats : JPG, PNG, WebP, GIF, MP4, WebM."
        action={cfg.slides.length < 3 && (
          <button onClick={addSlide} data-testid="promo-add-slide"
            className="flex items-center gap-1.5 rounded-full border border-line px-3.5 py-2 text-xs font-bold text-ink hover:border-brand hover:text-brand">
            <Plus className="h-3.5 w-3.5" /> Ajouter un visuel
          </button>
        )}>
        {cfg.slides.length === 0 ? (
          <p className="py-8 text-center text-sm text-slate-500">Aucun visuel. Cliquez sur « Ajouter un visuel » pour commencer.</p>
        ) : (
          <div className="grid gap-5 lg:grid-cols-3">
            {cfg.slides.map((s, i) => (
              <div key={i} className="flex flex-col gap-3 rounded-2xl border border-line bg-white p-4" data-testid={`promo-slide-${i}`}>
                <div className="relative flex aspect-square items-center justify-center overflow-hidden rounded-xl bg-slate-900">
                  {s.image_url ? (
                    s.media_kind === "video" ? (
                      <video src={fileUrl(s.image_url)} muted loop autoPlay playsInline className="h-full w-full object-contain" />
                    ) : (
                      <img src={fileUrl(s.image_url)} alt="" className="h-full w-full object-contain" />
                    )
                  ) : (
                    <div className="flex flex-col items-center gap-1 text-slate-500"><ImageIcon className="h-8 w-8" /><span className="text-xs">Aucun visuel</span></div>
                  )}
                </div>
                <input ref={(el) => (fileRefs.current[i] = el)} type="file" className="hidden"
                  accept="image/png,image/jpeg,image/webp,image/gif,video/mp4,video/webm,video/quicktime"
                  data-testid={`promo-file-${i}`} onChange={(e) => upload(i, e.target.files?.[0])} />
                <button type="button" onClick={() => fileRefs.current[i]?.click()} disabled={uploadingIdx === i}
                  data-testid={`promo-upload-${i}`}
                  className="flex items-center justify-center gap-2 rounded-lg border border-line py-2 text-xs font-bold text-ink hover:border-brand hover:text-brand disabled:opacity-60">
                  {uploadingIdx === i ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />}
                  {uploadingIdx === i ? "Téléversement…" : "Téléverser un visuel"}
                </button>
                <label className="block">
                  <span className="mb-1 flex items-center gap-1 text-[11px] font-bold uppercase tracking-wide text-slate-500"><Link2 className="h-3 w-3" /> Lien au clic (optionnel)</span>
                  <input className={inputCls} placeholder="/promotions ou https://…" value={s.link_url}
                    onChange={(e) => setSlide(i, "link_url", e.target.value)} data-testid={`promo-link-${i}`} />
                </label>
                <div className="flex items-center gap-3">
                  <label className="flex-1">
                    <span className="mb-1 block text-[11px] font-bold uppercase tracking-wide text-slate-500">Durée (s)</span>
                    <input type="number" min={1} max={60} className={inputCls} value={s.duration_seconds}
                      onChange={(e) => setSlide(i, "duration_seconds", e.target.value)} data-testid={`promo-duration-${i}`} />
                  </label>
                  <label className="flex flex-col items-center gap-1">
                    <span className="text-[11px] font-bold uppercase tracking-wide text-slate-500">Actif</span>
                    <Switch checked={s.is_active !== false} onCheckedChange={(v) => setSlide(i, "is_active", v)} data-testid={`promo-active-${i}`} />
                  </label>
                  <button type="button" onClick={() => removeSlide(i)} data-testid={`promo-remove-${i}`}
                    className="mt-4 flex h-9 w-9 items-center justify-center rounded-lg text-rose-600 hover:bg-rose-50">
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
        <p className="mt-4 flex items-center gap-2 rounded-xl bg-brand-light px-3 py-2 text-xs font-semibold text-brand">
          <Eye className="h-3.5 w-3.5" /> Astuce : ouvrez le site dans un nouvel onglet pour voir la fenêtre. Elle s'affiche une fois par session (désactivez l'option ci-dessus pour la revoir à chaque page).
        </p>
      </AdminCard>
    </div>
  );
}
